/**
 * StoreToApp bridge SDK — v0.1 (POC)
 * assets/storetoapp.js
 *
 * Lets a storefront page talk to the StoreToApp mobile app when it is
 * loaded inside the app's experience container. Safe to ship on the web:
 * outside the app, StoreToApp.isApp is false and every call rejects
 * with 'not_in_app', so guarded code paths simply never run.
 *
 * Protocol (v1.0):
 *   page -> app : StoreToAppBridge.postMessage(JSON {version, id, action, payload})
 *   app -> page : CustomEvent 'storetoapp:response' with detail
 *                 {version, id, success, data, error}
 */
(function () {
  'use strict';

  var BRIDGE_VERSION = '1.0';
  var REQUEST_TIMEOUT_MS = 10000;
  var pending = {};
  var seq = 0;

  function hasBridge() {
    return typeof window.StoreToAppBridge !== 'undefined'
      && typeof window.StoreToAppBridge.postMessage === 'function';
  }

  function call(action, payload) {
    return new Promise(function (resolve, reject) {
      if (!hasBridge()) {
        reject(new Error('not_in_app'));
        return;
      }
      seq += 1;
      var id = 'req-' + Date.now().toString(36) + '-' + seq;
      var entry = { resolve: resolve, reject: reject, timer: null };
      // a lost message must never hang the page
      entry.timer = setTimeout(function () {
        if (pending[id]) {
          delete pending[id];
          reject(new Error('timeout'));
        }
      }, REQUEST_TIMEOUT_MS);
      pending[id] = entry;

      window.StoreToAppBridge.postMessage(JSON.stringify({
        version: BRIDGE_VERSION,
        id: id,
        action: action,
        payload: payload || {}
      }));
    });
  }

  window.addEventListener('storetoapp:response', function (e) {
    var msg = e.detail || {};
    var entry = pending[msg.id];
    if (!entry) return;
    clearTimeout(entry.timer);
    delete pending[msg.id];
    if (msg.success) {
      entry.resolve(msg.data);
    } else {
      entry.reject(new Error((msg.error && msg.error.code) || 'bridge_error'));
    }
  });

  window.StoreToApp = {
    /** true only when the page runs inside the StoreToApp app container */
    get isApp() { return hasBridge(); },

    /** handshake — resolves with {platform, locale, currency, capabilities} */
    ready: function () { return call('bridge.handshake'); },

    cart: {
      /**
       * addLines({ lines: [{ variantId, quantity }] })
       * variantId may be a numeric Shopify id or a full GID;
       * the app normalizes it. Resolves with a cart summary.
       */
      addLines: function (payload) { return call('cart.addLines', payload); },

      /** opens the app's native cart screen */
      open: function () { return call('cart.open'); }
    },

    /** open a URL outside the container (external browser / WhatsApp etc.) */
    openExternal: function (url) { return call('navigation.openExternal', { url: url }); }
  };
})();
